import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import InvoicesDueAlert from './InvoicesDueAlert';
import Limit90Alert from './Limit90Alert';
import OpenInvoicesAlert from './OpenInvoicesAlert';
import Limit100Alert from './Limit100Alert';
import { Alert, Button, Snackbar, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NoActiveSubscriptionAlert from './NoActiveSubscriptionAlert';
import { ShopifyIntegrationBanner } from './ShopifyIntegrationBanner';

const snackbarStyles = {
  position: 'relative',
  '&.MuiSnackbar-root': {
    width: '100%',
    bottom: '0',
    '& .MuiPaper-root': {
      width: '100%',
      justifyContent: 'center',
    },
  },
  '& .MuiAlert-icon': {
    alignItems: 'center',
  },
};

const BottomBar = () => {
  const subscription = useSelector((state) => state.org.org.subscription);
  const navigate = useNavigate();
  const isShopifyConnected = useSelector((state) => state.org.org.shopifyDisconnectedShopExists);
  const [openSnackbar, setOpenSnackbar] = useState({
    invoicesDue: false,
    noActiveSubscription: false,
    limit100: false,
    openInvoices: false,
    limit90: false,
    shopify_enabled: false,
    shopifyDisconnectedShopExists: false,
  });
  const [shopifyfeatureEnabled, setShopifyfeatureEnabled] = useState(false);

  useEffect(() => {
    if (!subscription) {
      return;
    }

    if (subscription?.features?.enable_shopify_integration === true) {
      setShopifyfeatureEnabled(true);
    }
    const isLimitReached =
      subscription.plan.type === 'prepaid'
        ? subscription?.currentInvoice?.documents_count >= subscription?.documents_limit
        : subscription.plan.type === 'postpaid' &&
          subscription.plan.pricing_model === 'licensed' &&
          subscription?.currentInvoice?.documents_count >= subscription?.documents_limit;

    const isLimit90 =
      subscription.plan.type === 'prepaid'
        ? subscription?.currentInvoice?.documents_count >= 0.9 * subscription?.documents_limit
        : subscription.plan.type === 'postpaid' &&
          subscription.plan.pricing_model === 'licensed' &&
          subscription?.currentInvoice?.documents_count >= 0.9 * subscription?.documents_limit;

    const shouldShowOpenInvoices =
      !subscription?.isFromPartner && subscription?.dueInvoices?.some((invoice) => invoice.status === 'open');

    if (subscription.status === 'past_due') {
      setOpenSnackbar({ invoicesDue: true, limit100: false, openInvoices: false, limit90: false });
    } else if (subscription.status !== 'active') {
      setOpenSnackbar({
        invoicesDue: false,
        limit100: false,
        openInvoices: false,
        limit90: false,
        noActiveSubscription: true,
      });
    } else if (isLimitReached) {
      setOpenSnackbar({ invoicesDue: false, limit100: true, openInvoices: false, limit90: false });
    } else if (shouldShowOpenInvoices) {
      setOpenSnackbar({ invoicesDue: false, limit100: false, openInvoices: true, limit90: false });
    } else if (isLimit90) {
      setOpenSnackbar({ invoicesDue: false, limit100: false, openInvoices: false, limit90: true });
    } else if (shopifyfeatureEnabled && !isShopifyConnected) {
      setOpenSnackbar({
        invoicesDue: false,
        limit100: false,
        openInvoices: false,
        limit90: false,
        shopify_enabled: true,
        shopifyDisconnectedShopExists: true,
      });
    }
  }, [subscription, shopifyfeatureEnabled]);

  const shouldShowSnackbar =
    openSnackbar.invoicesDue ||
    openSnackbar.limit100 ||
    openSnackbar.limit90 ||
    openSnackbar.openInvoices ||
    openSnackbar.noActiveSubscription;

  const showShopifyBanner = openSnackbar.shopify_enabled && openSnackbar.shopifyDisconnectedShopExists;

  return (
    <Snackbar
      open={shouldShowSnackbar || showShopifyBanner}
      sx={snackbarStyles}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="warning">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            {openSnackbar.invoicesDue && (
              <InvoicesDueAlert isFromPartner={subscription?.isFromPartner} partnerEmail={subscription?.partnerEmail} />
            )}
            {openSnackbar.shopify_enabled && openSnackbar.shopifyDisconnectedShopExists && (
              <ShopifyIntegrationBanner
                shopify_enabled={shopifyfeatureEnabled}
                isShopifyConnected={isShopifyConnected}
                onClose={() =>
                  setOpenSnackbar({ ...openSnackbar, shopify_enabled: false, shopifyDisconnectedShopExists: false })
                }
              />
            )}
            {openSnackbar.openInvoices && (
              <OpenInvoicesAlert
                dueInvoices={subscription?.dueInvoices}
                onClose={() => setOpenSnackbar({ ...openSnackbar, openInvoices: false })}
              />
            )}
            {openSnackbar.limit90 && (
              <Limit90Alert
                isFromPartner={subscription?.isFromPartner}
                partnerEmail={subscription?.partnerEmail}
                onClose={() => setOpenSnackbar({ ...openSnackbar, limit90: false })}
              />
            )}
            {openSnackbar.limit100 && (
              <Limit100Alert isFromPartner={subscription?.isFromPartner} partnerEmail={subscription?.partnerEmail} />
            )}
            {openSnackbar.noActiveSubscription && (
              <NoActiveSubscriptionAlert
                isFromPartner={subscription?.isFromPartner}
                partnerEmail={subscription?.partnerEmail}
              />
            )}
          </Stack>
          {!subscription?.isFromPartner && shouldShowSnackbar && (
            <Button color="primary" variant="contained" onClick={() => navigate('/settings')}>
              Manage Subscriptions
            </Button>
          )}
        </Stack>
      </Alert>
    </Snackbar>
  );
};

export default BottomBar;
